import React from 'react';
import SEO from '../components/common/SEO';
import GlobalPresence from '../components/common/GlobalPresence';
import { Container, Row, Col, Badge, Card, Carousel } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useScrollToTop } from '../hooks/useScrollToTop';
import './Home.css';

const Home: React.FC = () => {
  const scrollToTop = useScrollToTop();

  // Actual Skytion Products - Updated with new slugs
  const products = [
    {
      title: "2 Ply Dining Tissue",
      image: "/assets/skytion/products/2-ply-tissue-paper.png",
      description: "Premium 2 ply dining tissue (30cm x 30cm) made from soft tissue material. Perfect for restaurants and dining applications.",
      size: "30cm x 30cm",
      ply: "2 Ply",
      link: "/products/2ply-premium-napkin"
    },
    {
      title: "Toilet Roll Soft 2 Ply",
      image: "/assets/skytion/products/toilet-paper.png",
      description: "Premium 2 ply soft toilet roll (4inch x 4inch, 480 gram). 6 in 1 pack for household and commercial use.",
      size: "4inch x 4inch",
      ply: "2 Ply",
      link: "/products/skysun-2ply-toilet-roll"
    },
    {
      title: "Kitchen Towel 2 Ply",
      image: "/assets/skytion/products/kitchen-towel-png.png",
      description: "Absorbent kitchen towel (4inch x 8inch, 240 gram) made from 2 ply soft material. Ideal for kitchen cleaning and spills.",
      size: "4inch x 8inch",
      ply: "2 Ply",
      link: "/products/kitchen-towel"
    },
    {
      title: "M Fold Tissue Soft",
      image: "/assets/skytion/products/m-fold-tissue.png",
      description: "Convenient M fold tissue (12 x 12 cm) made from soft material. Perfect for washrooms and office dispensers.",
      size: "12 x 12 cm",
      ply: "Single Ply",
      link: "/products/m-fold-tissue"
    },
    {
      title: "C Fold Tissue Soft",
      image: "/assets/skytion/products/c-fold-tissue-paper.png",
      description: "Premium C fold tissue (20 x 28 cm) made from soft material. Ideal for high-traffic washrooms and commercial spaces.",
      size: "20 x 28 cm",
      ply: "Single Ply",
      link: "/products/c-fold-tissue"
    },
    {
      title: "1 Ply Single Tissue",
      image: "/assets/skytion/products/1-ply-single-tissue.png",
      description: "Economical 1 ply single tissue (27cm x 30cm) made from soft tissue material. Cost-effective solution for basic needs.",
      size: "27cm x 30cm",
      ply: "1 Ply",
      link: "/products/single-ply-tissue"
    },
    {
      title: "HRT Tissue Paper",
      image: "/assets/skytion/products/HTR-tissue-paper.jpg",
      description: "High-quality HRT (Hard Rolled Tissue) paper for industrial and commercial applications.",
      size: "Standard Roll",
      ply: "Single Ply",
      link: "/products/hrt-roll"
    },
    {
      title: "Mini Tissue Papers",
      image: "/assets/skytion/products/skytion-mini-tissue-papers.png",
      description: "Compact mini tissue papers perfect for travel and on-the-go use. Convenient pocket-sized packaging.",
      size: "Mini Size",
      ply: "2 Ply",
      link: "/products/mini-tissue"
    },
    {
      title: "Tissue Packet",
      image: "/assets/skytion/products/skytion-tissue-packet.png",
      description: "Individual tissue packets for personal use and retail distribution. Hygienic and convenient packaging.",
      size: "Pocket Pack",
      ply: "2 Ply",
      link: "/products/skysun-facial-tissue-box"
    },
  ];

  return (
    <>
      <SEO
        title="Home"
        description="Skytion Enterprises (Est. 2022) - Premium tissue manufacturing from virgin & recycled pulp. Skysun brand serving India, USA, Indonesia with 9 product lines from our Noida facility."
        keywords="skytion enterprises, skysun brand, tissue manufacturing, toilet roll, kitchen towel, dining tissue, m fold, c fold, noida, 2022, women workforce"
      />

      {/* Clean Hero Section */}
      <section
        className="hero-section-clean hero-section-tissue position-relative tissue-animated"
        style={{
          backgroundImage: 'url(/assets/skytion/ui/hero-background.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {/* Background Overlay */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
            zIndex: 1
          }}
        ></div>

        <Container style={{ position: 'relative', zIndex: 2 }}>
          <Row className="align-items-center min-vh-100">
            <Col lg={6}>
              <div className="hero-content text-center text-lg-start">
                <img
                  src="/assets/skytion/brand/skytion logo png.png"
                  alt="Skytion Enterprises"
                  className="mb-4"
                  style={{ maxWidth: '300px' }}
                />

                <div className="mb-4">
                  <Badge bg="primary" className="me-2 mb-2">Est. 2022</Badge>
                  <Badge bg="success" className="me-2 mb-2">ISO Certified</Badge>
                  <Badge bg="info" className="mb-2">Global Export</Badge>
                </div>

                <h1 className="display-2 mb-4 fw-bold" style={{ color: '#2c5282' }}>
                  Premium Tissue<br />
                  <span style={{ color: '#22c55e' }}>Manufacturing</span>
                </h1>

                <p className="lead mb-4" style={{ color: '#4b5563' }}>
                  Skytion Enterprises delivers world-class tissue products through our two specialized brands:
                  <strong> SKYSUN</strong> (Premium) and <strong>SKYTION</strong> (Economy).
                  Serving UK, USA, UAE, France, and India with 10+ product lines from our state-of-the-art Noida facility.
                </p>

                <div className="d-flex gap-3 justify-content-center justify-content-lg-start flex-wrap">
                  <Link
                    to="/contact"
                    className="btn btn-lg px-4 py-3"
                    style={{
                      backgroundColor: '#22c55e',
                      borderColor: '#22c55e',
                      color: 'white',
                      borderRadius: '12px',
                      fontWeight: '600'
                    }}
                    onClick={scrollToTop}
                  >
                    Get Quote
                  </Link>
                  <Link
                    to="/products"
                    className="btn btn-outline-dark btn-lg px-4 py-3"
                    style={{
                      borderRadius: '12px',
                      fontWeight: '600'
                    }}
                    onClick={scrollToTop}
                  >
                    View Products
                  </Link>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <div className="hero-stats mt-5 mt-lg-0">
                <Row className="g-3">
                  <Col md={6}>
                    <Card className="text-center border-0 shadow-sm h-100 card-tissue-bg">
                      <Card.Body className="p-4">
                        <h3 className="text-primary mb-2">2</h3>
                        <h6 className="mb-1">Brands</h6>
                        <small className="text-muted">Complete Portfolio</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="text-center border-0 shadow-sm h-100 card-tissue-bg">
                      <Card.Body className="p-4">
                        <h3 className="text-success mb-2">Global</h3>
                        <h6 className="mb-1">Shipping</h6>
                        <small className="text-muted">Worldwide Delivery</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="text-center border-0 shadow-sm h-100 card-tissue-bg">
                      <Card.Body className="p-4">
                        <h3 className="text-info mb-2">99.9%</h3>
                        <h6 className="mb-1">Quality Assurance</h6>
                        <small className="text-muted">ISO Standards</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="text-center border-0 shadow-sm h-100 card-tissue-bg">
                      <Card.Body className="p-4">
                        <h3 className="text-warning mb-2">24/7</h3>
                        <h6 className="mb-1">Production</h6>
                        <small className="text-muted">Advanced Technology</small>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Global Presence Banner */}
      <GlobalPresence />

      {/* Catalogue & Eco-Packaging Showcase (Tailwind/Vercel inspired) */}
      <section className="py-24 bg-slate-50 border-y border-slate-200">
        <Container>
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2 space-y-8">
              <div className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-sm font-medium text-slate-800 shadow-sm">
                <span className="flex h-2 w-2 rounded-full bg-green-500 mr-2"></span>
                New Release
              </div>
              <h2 className="text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 leading-tight">
                Complete Eco-Friendly <br />
                Packaging Solutions.
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl">
                Discover our newly expanded commercial catalog. We now offer premium Kraft Paper Cups, Ripple Cups, and Bagasse Food Containers alongside our signature tissue products. Sustainable, durable, and fully customizable.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a
                  href="/SKYTION-Catalogue.pdf"
                  download
                  className="inline-flex items-center justify-center rounded-md bg-slate-900 px-6 py-3 text-sm font-medium text-white shadow transition-colors hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 no-underline"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                  Download 21-Page Catalogue
                </a>
                <Link
                  to="/products"
                  onClick={scrollToTop}
                  className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-6 py-3 text-sm font-medium text-slate-900 shadow-sm transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 no-underline"
                >
                  Explore Categories
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-6 pt-8 border-t border-slate-200">
                <div>
                  <h4 className="text-3xl font-bold text-slate-900 tracking-tight">16+</h4>
                  <p className="text-sm text-slate-500 mt-1">Product Categories</p>
                </div>
                <div>
                  <h4 className="text-3xl font-bold text-slate-900 tracking-tight">100%</h4>
                  <p className="text-sm text-slate-500 mt-1">Eco-Friendly Options</p>
                </div>
              </div>
            </div>

            <div className="lg:w-1/2 w-full">
              <div className="relative rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
                <div className="absolute inset-0 bg-gradient-to-tr from-green-50 to-blue-50 opacity-50 rounded-2xl"></div>
                <div className="relative rounded-xl overflow-hidden border border-slate-100 bg-slate-50 aspect-[4/3] flex items-center justify-center group">
                  <div className="absolute inset-0 bg-slate-900/5 group-hover:bg-transparent transition-colors duration-500 z-10"></div>
                  {/* We use the catalogue cover image here. Assuming it exists or we use a clean generic product stack */}
                  <img
                    src="/assets/skytion/products/skytion-mini-tissue-papers.png"
                    alt="Eco-Friendly Products Assortment"
                    className="w-3/4 h-auto object-contain drop-shadow-2xl group-hover:scale-105 transition-transform duration-700 ease-out"
                  />

                  {/* Floating badges */}
                  <div className="absolute top-6 right-6 z-20 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-sm border border-slate-200 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                    <span className="text-xs font-medium text-slate-700">Food Safe</span>
                  </div>
                  <div className="absolute bottom-6 left-6 z-20 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-sm border border-slate-200 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M16 12l-4 4-4-4M12 8v8"></path></svg>
                    <span className="text-xs font-medium text-slate-700">Compostable</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Enhanced Visual Statistics Section */}
      <section className="stats-section-enhanced py-5 bg-white">
        <Container>
          <Row className="justify-content-center mb-5">
            <Col lg={8} className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Badge bg="primary" className="px-4 py-2 fs-6 mb-4">Our Impact</Badge>
                <h2 className="display-4 mb-4">Delivering Excellence Worldwide</h2>
                <p className="lead text-muted">
                  Trusted by businesses across five countries for premium tissue manufacturing solutions
                </p>
              </motion.div>
            </Col>
          </Row>

          <Row className="g-4">
            <Col lg={3} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="stat-card-enhanced text-center h-100 border-0 shadow-lg">
                  <Card.Body className="p-4">
                    <div className="stat-icon-enhanced mb-3 mx-auto">
                      <img src="/assets/skytion/icons/Softness Icon.png" alt="Quality" />
                    </div>
                    <h2 className="display-4 text-primary mb-2">99.9%</h2>
                    <h5 className="mb-2">Quality Assurance</h5>
                    <p className="text-muted small">ISO certified manufacturing with rigorous quality control</p>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>

            <Col lg={3} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <Card className="stat-card-enhanced text-center h-100 border-0 shadow-lg">
                  <Card.Body className="p-4">
                    <div className="stat-icon-enhanced mb-3 mx-auto">
                      <img src="/assets/skytion/icons/Durability Icon.png" alt="Production" />
                    </div>
                    <h2 className="display-4 text-success mb-2">24/7</h2>
                    <h5 className="mb-2">Production</h5>
                    <p className="text-muted small">Continuous manufacturing to meet global demand</p>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>

            <Col lg={3} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <Card className="stat-card-enhanced text-center h-100 border-0 shadow-lg">
                  <Card.Body className="p-4">
                    <div className="stat-icon-enhanced mb-3 mx-auto">
                      <img src="/assets/skytion/icons/Absorbency Icon.png" alt="Products" />
                    </div>
                    <h2 className="display-4 text-info mb-2">10+</h2>
                    <h5 className="mb-2">Product Lines</h5>
                    <p className="text-muted small">Across 2 brands serving all market segments</p>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>

            <Col lg={3} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <Card className="stat-card-enhanced text-center h-100 border-0 shadow-lg">
                  <Card.Body className="p-4">
                    <div className="stat-icon-enhanced mb-3 mx-auto">
                      <img src="/assets/skytion/brand/GOLD_RIM-removebg-preview.png" alt="Countries" style={{ width: '50px' }} />
                    </div>
                    <h2 className="display-4 text-warning mb-2">5</h2>
                    <h5 className="mb-2">Countries</h5>
                    <p className="text-muted small">UK, USA, UAE, France, and India markets served</p>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Two Brands Section */}
      <section className="py-5">
        <Container>
          <Row className="justify-content-center mb-5">
            <Col lg={8} className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Badge bg="primary" className="px-4 py-2 fs-6 mb-4">Our Brands</Badge>
                <h2 className="display-4 mb-4">Two Brands, Every Need</h2>
                <p className="lead text-muted">
                  A complete multi-brand portfolio serving premium retail to economy bulk markets
                </p>
              </motion.div>
            </Col>
          </Row>

          <Row className="g-4">
            <Col lg={4} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-100 border-0 shadow-lg" style={{ borderTop: '4px solid #FF6B35' }}>
                  <Card.Body className="p-4 text-center">
                    <div className="mb-3">
                      <div
                        className="mx-auto rounded-circle d-flex align-items-center justify-content-center mb-3"
                        style={{
                          width: '80px',
                          height: '80px',
                          backgroundColor: '#FF6B35',
                          color: 'white',
                          fontSize: '2rem'
                        }}
                      >
                        ★
                      </div>
                      <h3 className="mb-2" style={{ color: '#FF6B35' }}>SKYSUN</h3>
                      <Badge bg="warning" text="dark">Premium Brand</Badge>
                    </div>
                    <p className="text-muted mb-3">
                      Premium quality tissue products made from virgin pulp.
                      Perfect for retail, hospitality, and export markets demanding the highest standards.
                    </p>
                    <div className="mb-3">
                      <small className="text-muted d-block">Target: Retail • Hotels • Export</small>
                    </div>
                    <Link to="/brands/skysun" className="btn btn-outline-primary w-100" onClick={scrollToTop}>
                      Explore SKYSUN
                    </Link>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>

            <Col lg={4} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <Card className="h-100 border-0 shadow-lg" style={{ borderTop: '4px solid #10B981' }}>
                  <Card.Body className="p-4 text-center">
                    <div className="mb-3">
                      <div
                        className="mx-auto rounded-circle d-flex align-items-center justify-content-center mb-3"
                        style={{
                          width: '80px',
                          height: '80px',
                          backgroundColor: '#10B981',
                          color: 'white',
                          fontSize: '2rem'
                        }}
                      >
                        ●
                      </div>
                      <h3 className="mb-2" style={{ color: '#10B981' }}>SKYTION</h3>
                      <Badge bg="success">Economy Brand</Badge>
                    </div>
                    <p className="text-muted mb-3">
                      Value-driven solutions for cost-conscious operations requiring high volume supply.
                      Best for bulk distribution and large-scale facilities.
                    </p>
                    <div className="mb-3">
                      <small className="text-muted d-block">Target: Bulk • Distribution • Economy</small>
                    </div>
                    <Link to="/brands/skytion" className="btn btn-outline-success w-100" onClick={scrollToTop}>
                      Explore SKYTION
                    </Link>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Interactive Product Showcase with Carousel */}
      <section className="product-carousel-section py-5 bg-light">
        <Container>
          <Row className="justify-content-center mb-5">
            <Col lg={8} className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Badge bg="success" className="px-4 py-2 fs-6 mb-4">Featured Products</Badge>
                <h2 className="display-4 mb-4">Premium Tissue Collection</h2>
                <p className="lead text-muted">
                  Explore our signature SKYSUN brand products crafted with precision and care
                </p>
              </motion.div>
            </Col>
          </Row>

          <Row>
            <Col lg={8} className="mx-auto">
              <Carousel
                className="product-carousel-enhanced"
                indicators={true}
                controls={true}
                interval={4000}
                fade={true}
              >
                {products.slice(0, 6).map((product, index) => (
                  <Carousel.Item key={index}>
                    <motion.div
                      className="carousel-product-card"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Card className="product-showcase-card border-0 shadow-lg">
                        <Card.Body className="p-5">
                          <Row className="align-items-center">
                            <Col md={6}>
                              <div className="product-image-showcase">
                                <img
                                  src={product.image}
                                  alt={product.title}
                                  className="img-fluid product-carousel-image"
                                />
                                <div className="product-glow-effect"></div>
                              </div>
                            </Col>
                            <Col md={6}>
                              <div className="product-info-enhanced">
                                <div className="product-badges mb-3">
                                  <Badge bg="primary" className="me-2">{product.ply}</Badge>
                                  <Badge bg="info">{product.size}</Badge>
                                </div>
                                <h3 className="mb-3">{product.title}</h3>
                                <p className="lead text-muted mb-4">{product.description}</p>
                                <Link
                                  to={product.link}
                                  className="btn btn-primary btn-lg"
                                  onClick={scrollToTop}
                                >
                                  View Details
                                  <i className="ms-2">→</i>
                                </Link>
                              </div>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </motion.div>
                  </Carousel.Item>
                ))}
              </Carousel>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Manufacturing Excellence Section */}
      <section className="manufacturing-section py-5 bg-light position-relative">
        <div className="section-overlay"></div>
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Badge bg="warning" className="px-4 py-2 fs-6 mb-4">Manufacturing</Badge>
                <h2 className="display-4 mb-4">State-of-the-Art Facility</h2>
                <p className="lead mb-4">
                  Our modern Noida facility combines cutting-edge technology with sustainable practices
                  to deliver premium tissue products at scale.
                </p>

                <Row className="g-3">
                  <Col sm={6}>
                    <Card className="feature-card-mini border-0 bg-white shadow-sm">
                      <Card.Body className="p-3">
                        <div className="d-flex align-items-center">
                          <div className="feature-icon-mini me-3">
                            <img src="/assets/skytion/brand/certification badge .png" alt="Certified" style={{ width: '30px' }} />
                          </div>
                          <div>
                            <h6 className="mb-1">ISO Certified</h6>
                            <small className="text-muted">Quality Standards</small>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col sm={6}>
                    <Card className="feature-card-mini border-0 bg-white shadow-sm">
                      <Card.Body className="p-3">
                        <div className="d-flex align-items-center">
                          <div className="feature-icon-mini me-3">
                            <img src="/assets/skytion/brand/premium quality tag.png" alt="Premium" style={{ width: '30px' }} />
                          </div>
                          <div>
                            <h6 className="mb-1">Premium Quality</h6>
                            <small className="text-muted">Virgin & Recycled Pulp</small>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="manufacturing-visual"
              >
                <div className="image-stack">
                  <div className="stack-image-1">
                    <img
                      src="/assets/skytion/company/Facility photo.png"
                      alt="Manufacturing Facility"
                      className="img-fluid rounded-4 shadow-lg"
                    />
                  </div>
                  <div className="stack-image-2">
                    <img
                      src="/assets/skytion/company/manucturing image.jpeg"
                      alt="Manufacturing Process"
                      className="img-fluid rounded-4 shadow-lg"
                    />
                  </div>
                </div>
                <div className="manufacturing-stats">
                  <Card className="stats-overlay">
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between">
                        <div className="text-center">
                          <h4 className="text-success mb-1">80%</h4>
                          <small>Women Workforce</small>
                        </div>
                        <div className="text-center">
                          <h4 className="text-primary mb-1">2022</h4>
                          <small>Established</small>
                        </div>
                        <div className="text-center">
                          <h4 className="text-info mb-1">24/7</h4>
                          <small>Operations</small>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>


      {/* Enhanced Product Showcase */}
      <section className="product-showcase-enhanced product-showcase-tissue py-5">
        <Container>
          <div className="showcase-header mb-5">
            <div className="section-badge text-center mb-4">
              <Badge bg="success" className="px-4 py-2 fs-6">Our Products</Badge>
            </div>
            <h2 className="display-4 text-center mb-4">9 Premium Product Lines</h2>
            <p className="lead text-center text-muted">
              From dining tissues to industrial rolls, discover our comprehensive range of
              high-quality tissue products manufactured with precision and care.
            </p>
          </div>

          <div className="product-grid">
            {products.slice(0, 6).map((product, index) => (
              <div key={index} className="product-card-enhanced">
                <div className="product-image-container">
                  <img src={product.image} alt={product.title} className="product-image" />
                </div>
                <div className="product-specs">
                  <Badge bg="primary" className="spec-badge">{product.ply}</Badge>
                  <Badge bg="info" className="spec-badge">{product.size}</Badge>
                </div>
                <h4 className="mb-3">{product.title}</h4>
                <p className="text-muted mb-4">{product.description}</p>
                <Link to={product.link} className="btn btn-outline-primary w-100" onClick={scrollToTop}>
                  View Details
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-5">
            <Link to="/products" className="btn btn-primary btn-lg" onClick={scrollToTop}>
              View All Products
            </Link>
          </div>
        </Container>
      </section>

      {/* Call to Action Section */}
      <section className="cta-section py-5">
        <Container>
          <div className="cta-content">
            <h2 className="display-4 mb-4">Ready to Partner with Excellence?</h2>
            <p className="lead mb-5">
              Join leading businesses worldwide who trust Skytion Enterprises for their tissue product needs.
              Get competitive bulk pricing and reliable global supply chain support.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-light btn-lg me-3 mb-3" onClick={scrollToTop}>
                Request Quote
              </Link>
              <Link to="/about" className="btn btn-outline-light btn-lg mb-3" onClick={scrollToTop}>
                Learn More
              </Link>
            </div>
            <div className="contact-highlight">
              <div className="contact-item">
                <span>Phone:</span>
                <span>+91 8920065965</span>
              </div>
              <div className="contact-item">
                <span>Email:</span>
                <span>sales@skytion.in</span>
              </div>
              <div className="contact-item">
                <span>Location:</span>
                <span>Noida, India</span>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
};

export default Home;